import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  changeUserActiveAdminService,
  changeUserPasswordAdminService,
  createTransactionAdminService,
  createUserAdminService,
  deleteUserAdminService,
  editUserAdminService,
  getUsersAdminService,
  verifyUserEmailAdminService,
  verifyUserPhoneNumberAdminService,
} from "./../../../services/adminServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { validateFile } from "../../../utils";
import { uploadTypeItems, userRoleItems } from "../../../utils/enum";
import { uploadUserService } from "../../../services/userServices";

export const userAdminContext = createContext({
  users: [],
  setUsers: () => {},
  user: {},
  setUser: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  role: "",
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleCreateUser: () => {},
  handleEditUser: () => {},
  handleChangeUserPassword: () => {},
  handleChangeUserActive: () => {},
  handleDeleteUser: () => {},
  handleUploadFile: () => {},
  handleCreateTransaction: () => {},
  handleVerifyUserPhoneNumber: () => {},
  handleVerifyUserEmail: () => {},
});

const UserAdminContext = ({ children, role = userRoleItems[0] }) => {
  const dispatch = useDispatch();

  const { agent } = useParams();

  const [users, setUsers] = useState([]);
  const [user, setUser] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setUser(val);
      setValue("avatar", val.avatar);
      setValue("firstName", val.firstName);
      setValue("lastName", val.lastName);
      setValue("phoneNumber", val.phoneNumber);
      setValue("nationalNumber", val.nationalNumber);
      setValue("code", val.code);
      setValue("birthDate", val.birthDate);
      setValue("post", val.post);
      setValue("numberOfCovered", val.numberOfCovered);
      setValue("permissions", val.permissions);
      setValue("email", val.email);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setUser({});
  };

  // Get Users
  const handleGetUsers = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(agent && { agent }),
      filter,
      pageNumber,
      sort,
      itemsCount,
      role,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        setUsers(data.result?.users);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create User
  const handleCreateUser = async (userData) => {
    const objData = {
      ...userData,
      role,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createUserAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit User
  const handleEditUser = async (userData) => {
    const objData = {
      id: user._id,
      ...userData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editUserAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Change User Password
  const handleChangeUserPassword = async (userData) => {
    const objData = {
      id: user._id,
      ...userData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeUserPasswordAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        setUser({});
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Change User Active
  const handleChangeUserActive = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeUserActiveAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete User
  const handleDeleteUser = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteUserAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Upload File
  const handleUploadFile = async (file, callback) => {
    const fileErrorMessage = validateFile(file);
    if (fileErrorMessage) return toastErrorMessage(fileErrorMessage);

    const objData = { file };
    const params = { destination: uploadTypeItems[1] };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await uploadUserService(objData, params);
      if (status === 200) {
        callback && callback(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Transaction
  const handleCreateTransaction = async (userData) => {
    const objData = {
      ...userData,
      user: user._id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createTransactionAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Verify User PhoneNumber
  const handleVerifyUserPhoneNumber = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await verifyUserPhoneNumberAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Verify User Email
  const handleVerifyUserEmail = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await verifyUserEmailAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <userAdminContext.Provider
      value={{
        users,
        setUsers,
        user,
        setUser,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        role,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleCreateUser,
        handleEditUser,
        handleChangeUserPassword,
        handleChangeUserActive,
        handleDeleteUser,
        handleUploadFile,
        handleCreateTransaction,
        handleVerifyUserPhoneNumber,
        handleVerifyUserEmail,
      }}
    >
      {children}
    </userAdminContext.Provider>
  );
};

export default UserAdminContext;
